import { Button, Popconfirm, Progress, Tooltip } from 'antd'
import { DeleteOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons'
import { getAdvisoryAiReportTypeDisplayName } from '@/features/advisory-ai/api'
import {
  getDaysAway,
  getMeetingDateDisplay,
  isMeetingFinished,
  isRealMeetingDate,
} from '@/features/advisory-ai/calls/utils'
import { CompanyAvatarCluster } from '@/features/advisory-ai/consolidated/components/CompanyAvatarCluster'
import { RevaluationMissingBadge } from '@/features/advisory-ai/components/RevaluationMissingBadge'
import { GROUP_STATUS_META } from '@/features/advisory-ai/consolidated/utils'
import type { EnrichedConsolidatedAnalysis } from '@/features/advisory-ai/hooks'
import { createAdvisoryAccessGuard } from '@/features/advisory-ai/access'

type ConsolidatedCardProps = {
  item: EnrichedConsolidatedAnalysis
  isDeleting: boolean
  onOpen: (advisoryCallId: string) => void
  onPresent: (advisoryCallId: string) => void
  onDelete: (advisoryCallId: string) => void
  /** Called instead of the action when this user is on none of the group's organisations. */
  onBlocked: () => void
}

function companyNames(item: EnrichedConsolidatedAnalysis): string {
  const names = item.group.orgs.map((o) => o.orgName)
  if (names.length <= 2) return names.join(', ')
  return `${names.slice(0, 2).join(', ')} +${names.length - 2}`
}

export function ConsolidatedCard({ item, isDeleting, onOpen, onPresent, onDelete, onBlocked }: ConsolidatedCardProps) {
  const { group, report, status, step } = item

  // A consolidated analysis needs access to just one of its companies - see CallCard.
  const { canInteract, blockedTooltip, guard } = createAdvisoryAccessGuard(report?.canInteract, onBlocked)
  const open = (id: string) => guard(() => onOpen(id))
  const present = (id: string) => guard(() => onPresent(id))
  const meta = GROUP_STATUS_META[status]
  const progressPercent = (step.current / step.total) * 100
  const meetingDate = report?.meetingDate ?? null
  const meetingFinished = isMeetingFinished(meetingDate)
  const daysAway = isRealMeetingDate(meetingDate) ? getDaysAway(meetingDate) : ''

  const reportTypes = (report?.reports ?? []).map((r) => getAdvisoryAiReportTypeDisplayName(r)).join(', ')
  const canPresent = status === 'ready' && !meetingFinished
  const headlinerTeaser = (report?.headlinerSummary ?? '').trim()

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${meta.borderColor} border-l-4 p-4 mb-4`}>
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <div className="bg-gray-100 px-3 py-1.5 rounded text-sm text-gray-700 font-medium border-l-4 border-gray-300">
          {getMeetingDateDisplay(meetingDate)}
        </div>
        <span className="font-medium text-gray-900">{report?.meetingTitle || 'Consolidated analysis'}</span>
        <div className="flex items-center gap-2 ml-auto">
          {report?.revaluationMissing ? <RevaluationMissingBadge consolidated /> : null}
          {daysAway && (
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${meta.tagColor}`}>{daysAway}</span>
          )}
          <span className="text-xs text-gray-500">
            {step.current}/{step.total} steps
          </span>
          <Tooltip title={canInteract ? 'View analysis' : blockedTooltip}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              aria-label="View analysis"
              className={canInteract ? undefined : 'opacity-50'}
              onClick={() => open(group.advisoryCallId)}
            />
          </Tooltip>
          {canInteract ? (
            <Popconfirm
              title="Delete consolidated analysis"
              description="This deletes the analysis for all its companies. This cannot be undone."
              onConfirm={() => onDelete(group.advisoryCallId)}
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
            >
              <Tooltip title="Delete">
                <Button type="text" icon={<DeleteOutlined />} size="small" danger loading={isDeleting} aria-label="Delete analysis" />
              </Tooltip>
            </Popconfirm>
          ) : (
            <Tooltip title={blockedTooltip}>
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                danger
                aria-label="Delete analysis"
                className="opacity-50"
                onClick={onBlocked}
              />
            </Tooltip>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <CompanyAvatarCluster names={group.orgs.map((o) => o.orgName)} sizeClass="w-7 h-7" textClass="text-xs" />
        <span className="text-sm text-gray-600">
          {group.orgs.length} companies · {companyNames(item)}
        </span>
        {reportTypes ? (
          <>
            <span className="text-gray-300">·</span>
            <span className="text-sm text-gray-500">{reportTypes}</span>
          </>
        ) : null}
      </div>

      {headlinerTeaser ? (
        <p className="text-sm text-gray-600 italic line-clamp-2 mb-2" title={headlinerTeaser}>
          {headlinerTeaser}
        </p>
      ) : null}

      <Progress percent={progressPercent} strokeColor={meta.progressColor} showInfo={false} size="small" className="mb-3" />

      <div className="flex items-center gap-2">
        {status === 'needsAttention' && (
          <Tooltip title={blockedTooltip}>
            <Button type="primary" className="feature-button-solid" onClick={() => open(group.advisoryCallId)}>
              Start Prep →
            </Button>
          </Tooltip>
        )}
        {status === 'inProgress' && (
          <Tooltip title={blockedTooltip}>
            <Button type="primary" className="feature-button-solid" onClick={() => open(group.advisoryCallId)}>
              Continue →
            </Button>
          </Tooltip>
        )}
        {canPresent && (
          <>
            <Tooltip title={blockedTooltip}>
              <Button onClick={() => open(group.advisoryCallId)}>Review</Button>
            </Tooltip>
            <Tooltip title={blockedTooltip}>
              <Button
                type="primary"
                style={{ backgroundColor: '#22c55e', borderColor: '#22c55e' }}
                icon={<DownloadOutlined />}
                onClick={() => present(group.advisoryCallId)}
              >
                Present
              </Button>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  )
}
